import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, User, Phone, Mail, MessageSquare, Check, ArrowRight, Smartphone, ExternalLink, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EVERFIT_INVITE_LINK = "https://invite.everfit.io/7DI0G65GXZ";

const BookingPage = () => {
  const [slots, setSlots] = useState({ time_slots: [], session_types: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    session_type: "consultation",
    notes: ""
  });

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const response = await axios.get(`${API}/booking/slots`);
        setSlots(response.data);
      } catch (error) {
        console.error("Error fetching slots:", error);
      }
    };
    fetchSlots();
  }, []);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const formatted = date.toISOString().split('T')[0];
    setFormData({ ...formData, date: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date || !formData.time) {
      toast.error("Please select a date and time");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, formData);
      setSubmitted(true);
      toast.success("Booking request submitted!");
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking. Please try again.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-32 pb-16 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 bg-mvision-gold text-black flex items-center justify-center">
              <Check size={40} />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              BOOKING CONFIRMED
            </h1>
            <p className="font-manrope text-neutral-400 text-lg mb-8">
              Your booking request has been submitted. We'll contact you within 24 hours to confirm your appointment.
            </p>
            <div className="bg-neutral-950 border border-mvision-gold/30 p-6 mb-8 text-left">
              <h3 className="font-oswald uppercase tracking-wider text-sm text-mvision-gold mb-4">BOOKING DETAILS</h3>
              <div className="space-y-3">
                <p className="font-manrope"><span className="text-neutral-500">Date:</span> {formData.date}</p>
                <p className="font-manrope"><span className="text-neutral-500">Time:</span> {formData.time}</p>
                <p className="font-manrope"><span className="text-neutral-500">Session:</span> {slots.session_types.find(s => s.id === formData.session_type)?.name || formData.session_type}</p>
              </div>
            </div>
            
            {/* Everfit CTA */}
            <div className="bg-gradient-to-r from-mvision-gold/20 to-mvision-gold/10 border border-mvision-gold/30 p-6 mb-8">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Smartphone size={20} className="text-mvision-gold" />
                <h3 className="font-oswald uppercase tracking-wider text-sm">GET READY FOR YOUR SESSION</h3>
              </div>
              <p className="font-manrope text-neutral-400 text-sm mb-4">
                Download <span className="text-mvision-gold font-semibold">Everfit</span> now to access your workouts and chat with your coach!
              </p>
              <a 
                href={EVERFIT_INVITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-mvision-gold text-black font-oswald uppercase tracking-wider text-sm px-6 py-3 hover:bg-mvision-goldLight transition-colors"
                data-testid="everfit-invite-btn"
              >
                JOIN EVERFIT
                <ExternalLink size={16} />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" data-testid="back-home-btn">
                <button className="btn-secondary">BACK TO HOME</button>
              </Link>
              <Link to="/assessment" data-testid="take-assessment-btn">
                <button className="btn-primary">TAKE ASSESSMENT</button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="py-16 md:py-20" data-testid="booking-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h1 className="font-oswald text-5xl md:text-6xl font-bold uppercase tracking-tighter mb-6 animate-fade-in">
              BOOK A SESSION
            </h1>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto">
              Schedule your free consultation or training session. Select a date and time that works best for you.
            </p>
          </div>
        </section>

        {/* Booking Form */}
        <section className="pb-16 md:pb-24 section-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-12" data-testid="booking-form">
              {/* Calendar Side */}
              <div>
                <h2 className="font-oswald text-2xl uppercase tracking-tighter mb-6">SELECT DATE & TIME</h2>
                
                {/* Calendar */}
                <div className="bg-neutral-950 border border-neutral-800 p-6 mb-6">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    className="mx-auto"
                    data-testid="booking-calendar"
                  />
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="animate-fade-in">
                    <h3 className="font-oswald uppercase tracking-wider text-sm text-neutral-500 mb-4">
                      AVAILABLE TIMES
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.time_slots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setFormData({ ...formData, time })}
                          className={`py-3 text-sm font-oswald uppercase tracking-wider transition-all duration-200 ${
                            formData.time === time
                              ? 'bg-white text-black'
                              : 'border border-neutral-700 text-neutral-400 hover:border-white hover:text-white'
                          }`}
                          data-testid={`time-slot-${time.replace(/\s|:/g, '-')}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Form Side */}
              <div>
                <h2 className="font-oswald text-2xl uppercase tracking-tighter mb-6">YOUR INFORMATION</h2>
                
                {/* Session Type */}
                <div className="mb-6">
                  <label className="font-oswald uppercase tracking-wider text-sm text-neutral-500 mb-3 block">
                    SESSION TYPE
                  </label>
                  <div className="space-y-3">
                    {slots.session_types.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, session_type: type.id })}
                        className={`w-full text-left p-4 transition-all duration-200 ${
                          formData.session_type === type.id
                            ? 'bg-white text-black'
                            : 'border border-neutral-700 hover:border-white'
                        }`}
                        data-testid={`session-type-${type.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-oswald uppercase tracking-tighter">{type.name}</span>
                          <span className={`text-sm ${formData.session_type === type.id ? 'text-neutral-600' : 'text-neutral-500'}`}>
                            {type.duration}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="space-y-4">
                  <div className="relative">
                    <User size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-600" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      className="input-field pl-8"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div className="relative">
                    <Mail size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-600" />
                    <input
                      type="email"
                      placeholder="Email Address"
                      className="input-field pl-8"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="booking-email-input"
                    />
                  </div>
                  <div className="relative">
                    <Phone size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-600" />
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      className="input-field pl-8"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="booking-phone-input"
                    />
                  </div>
                  <div className="relative">
                    <MessageSquare size={18} className="absolute left-0 top-4 text-neutral-600" />
                    <textarea
                      placeholder="Any notes or special requests (optional)"
                      className="input-field pl-8 min-h-[100px] resize-none"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      data-testid="booking-notes-input"
                    ></textarea>
                  </div>
                </div>

                {/* Summary */}
                {formData.date && formData.time && (
                  <div className="bg-neutral-950 border border-neutral-800 p-4 mt-6 animate-fade-in">
                    <h3 className="font-oswald uppercase tracking-wider text-sm text-neutral-500 mb-2">BOOKING SUMMARY</h3>
                    <p className="font-manrope text-sm">
                      <span className="text-neutral-500">Date:</span> {formData.date}
                    </p>
                    <p className="font-manrope text-sm">
                      <span className="text-neutral-500">Time:</span> {formData.time}
                    </p>
                    <p className="font-manrope text-sm">
                      <span className="text-neutral-500">Session:</span> {slots.session_types.find(s => s.id === formData.session_type)?.name}
                    </p>
                  </div>
                )}

                {/* Submit */}
                <button 
                  type="submit" 
                  className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                  disabled={loading || !formData.date || !formData.time}
                  data-testid="booking-submit-btn"
                >
                  {loading ? "SUBMITTING..." : (
                    <>
                      CONFIRM BOOKING
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BookingPage;
